<template>
  <PDialog
    v-model:visible="visible"
    :header="edicao ? 'Editar Parcela' : 'Nova Parcela'"
    :modal="true"
    :closable="true"
    :style="{ width: '700px' }"
    @hide="fecharDialog"
  >
    <div class="dialog-parcela-content">
      <!-- Linha 1: Número da Parcela e Ordem de Serviço -->
      <div class="row">
        <div class="input-container col-6">
          <div class="g-input-label">
            Nº Parcela <strong class="asterisco-obrigatorio">*</strong>
          </div>
          <PInputNumber
            class="input"
            v-model="parcelaForm.numeroParcela"
            :min="1"
            :disabled="edicao"
            placeholder="Auto-preenchido"
          />
          <small class="p-text-secondary" v-if="!edicao">
            ℹ️ Número auto-gerado sequencialmente
          </small>
        </div>

        <div class="input-container col-6">
          <div class="g-input-label">Nº Ordem de Serviço (OS)</div>
          <PInputText
            class="input"
            v-model="parcelaForm.numeroOrdemServico"
            :maxlength="6"
            placeholder="Ex: 123456"
          />
          <small class="p-text-secondary">
            ℹ️ Campo opcional
          </small>
        </div>
      </div>

      <!-- Linha 2: Quantidades -->
      <div class="row">
        <div class="input-container col-6">
          <div class="g-input-label">
            Quantidade Caixas <strong class="asterisco-obrigatorio">*</strong>
          </div>
          <PInputNumber
            class="input"
            v-model="parcelaForm.quantidadeCaixas"
            :min="1"
            placeholder="Qtd de caixas"
            @input="calcularPercentual"
          />
        </div>

        <div class="input-container col-6">
          <div class="g-input-label">
            Quantidade Folhas <strong class="asterisco-obrigatorio">*</strong>
          </div>
          <PInputNumber
            class="input"
            v-model="parcelaForm.quantidadeFolhas"
            :min="1"
            placeholder="Qtd de folhas"
          />
        </div>
      </div>

      <!-- Linha 3: Datas -->
      <div class="row">
        <div class="input-container col-6">
          <div class="g-input-label">
            Data Carregamento Suprema <strong class="asterisco-obrigatorio">*</strong>
          </div>
          <g-date-input
            class="input"
            v-model="parcelaForm.dataCarregamentoSuprema"
            @change="validarDatas"
          />
        </div>

        <div class="input-container col-6">
          <div class="g-input-label">
            Data Entrega Jauense <strong class="asterisco-obrigatorio">*</strong>
          </div>
          <g-date-input
            class="input"
            v-model="parcelaForm.dataEntregaImpressaoJauense"
            @change="validarDatas"
          />
        </div>
      </div>

      <!-- Alerta de Divergência de Datas -->
      <PMessage
        v-if="possuiDivergenciaData"
        severity="warn"
        :closable="false"
        style="margin-top: 16px;"
      >
        <strong>⚠️ Atenção:</strong> A data de entrega da Jauense diverge da data de carregamento da Suprema.
        Idealmente, a data de entrega deve ser anterior à data de carregamento.
      </PMessage>

      <!-- Info do Percentual Calculado -->
      <div class="info-percentual" v-if="parcelaForm.percentualParcela !== null">
        <i class="pi pi-info-circle"></i>
        <span>
          Esta parcela representa
          <strong>{{ parcelaForm.percentualParcela?.toFixed(2) }}%</strong>
          do total do pedido ({{ quantidadeTotalPedido }} caixas)
        </span>
      </div>
    </div>

    <template #footer>
      <PButton
        label="Cancelar"
        icon="pi pi-times"
        class="p-button-text"
        @click="fecharDialog"
      />
      <PButton
        label="Salvar"
        icon="pi pi-check"
        @click="salvarParcela"
        :disabled="!formularioValido"
      />
    </template>
  </PDialog>
</template>

<script>
export default {
  name: 'DialogParcela',

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    parcela: {
      type: Object,
      default: null
    },
    quantidadeTotalPedido: {
      type: Number,
      required: true
    },
    parcelasExistentes: {
      type: Array,
      default: () => []
    }
  },

  emits: ['update:modelValue', 'salvar'],

  data() {
    return {
      parcelaForm: {
        id: null,
        numeroParcela: null,
        quantidadeCaixas: null,
        quantidadeFolhas: null,
        dataCarregamentoSuprema: null,
        dataEntregaImpressaoJauense: null,
        numeroOrdemServico: null,
        percentualParcela: null,
        possuiDivergenciaData: false
      },
      edicao: false
    }
  },

  computed: {
    /**
     * v-model binding para visibilidade do dialog
     */
    visible: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },

    /**
     * Valida se data de entrega >= data de carregamento (divergência)
     * Data entrega DEVE ser ANTERIOR à data carregamento
     * @returns {boolean} True se houver divergência
     */
    possuiDivergenciaData() {
      if (!this.parcelaForm.dataEntregaImpressaoJauense ||
          !this.parcelaForm.dataCarregamentoSuprema) {
        return false
      }

      const dataEntrega = new Date(this.parcelaForm.dataEntregaImpressaoJauense)
      const dataCarregamento = new Date(this.parcelaForm.dataCarregamentoSuprema)

      // Divergência = data entrega >= data carregamento
      return dataEntrega >= dataCarregamento
    },

    /**
     * Valida se o formulário está completo
     * Nota: Divergência de data não bloqueia, apenas alerta
     * @returns {boolean} True se válido
     */
    formularioValido() {
      return this.parcelaForm.numeroParcela &&
             this.parcelaForm.quantidadeCaixas &&
             this.parcelaForm.quantidadeCaixas > 0 &&
             this.parcelaForm.quantidadeFolhas &&
             this.parcelaForm.quantidadeFolhas > 0 &&
             this.parcelaForm.dataCarregamentoSuprema &&
             this.parcelaForm.dataEntregaImpressaoJauense
    }
  },

  watch: {
    /**
     * Watcher para prop parcela
     * Preenche formulário quando editando
     */
    parcela: {
      immediate: true,
      handler(novaParcela) {
        if (novaParcela) {
          this.parcelaForm = { ...novaParcela }
          this.edicao = true
        } else {
          this.resetarFormulario()
          this.edicao = false
        }
      }
    },

    /**
     * Watcher para atualizar divergência de data no form
     */
    possuiDivergenciaData(novaDivergencia) {
      this.parcelaForm.possuiDivergenciaData = novaDivergencia
    }
  },

  methods: {
    /**
     * Calcula percentual da parcela baseado na quantidade de CAIXAS
     * Percentual = (quantidadeCaixas / quantidadeTotalPedido) × 100
     */
    calcularPercentual() {
      if (this.quantidadeTotalPedido && this.parcelaForm.quantidadeCaixas) {
        this.parcelaForm.percentualParcela =
          (this.parcelaForm.quantidadeCaixas / this.quantidadeTotalPedido) * 100
      } else {
        this.parcelaForm.percentualParcela = null
      }
    },

    /**
     * Valida datas (validação automática via computed possuiDivergenciaData)
     */
    validarDatas() {
      // Validação feita automaticamente pelo computed
      // Este método existe para ser chamado pelo @change dos date-inputs
    },

    /**
     * Salva parcela e emite evento
     * Alerta sobre divergência de data mas permite continuar
     */
    salvarParcela() {
      if (!this.formularioValido) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Validação',
          detail: 'Preencha todos os campos obrigatórios corretamente',
          life: 3000
        })
        return
      }

      // Atualizar flag de divergência antes de salvar
      this.parcelaForm.possuiDivergenciaData = this.possuiDivergenciaData

      // Alerta se houver divergência, mas permite salvar
      if (this.possuiDivergenciaData) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Divergência de Data',
          detail: 'Parcela salva com divergência entre data de entrega e data de carregamento',
          life: 4000
        })
      }

      this.$emit('salvar', { ...this.parcelaForm })
      this.fecharDialog()
    },

    /**
     * Fecha dialog e reseta formulário
     */
    fecharDialog() {
      this.visible = false
      this.resetarFormulario()
    },

    /**
     * Reseta formulário para valores iniciais
     */
    resetarFormulario() {
      this.parcelaForm = {
        id: null,
        numeroParcela: this.proximoNumeroParcela(),
        quantidadeCaixas: null,
        quantidadeFolhas: null,
        dataCarregamentoSuprema: null,
        dataEntregaImpressaoJauense: null,
        numeroOrdemServico: null,
        percentualParcela: null,
        possuiDivergenciaData: false
      }
    },

    /**
     * Calcula o próximo número de parcela baseado nas existentes
     * @returns {number} Próximo número sequencial
     */
    proximoNumeroParcela() {
      if (!this.parcelasExistentes || this.parcelasExistentes.length === 0) {
        return 1
      }

      const maior = Math.max(...this.parcelasExistentes.map(p => p.numeroParcela))
      return maior + 1
    }
  }
}
</script>

<style scoped>
.dialog-parcela-content {
  padding: 10px 0;
}

.row {
  display: flex;
  margin-bottom: 16px;
  gap: 16px;
}

.input-container {
  display: flex;
  flex-direction: column;
}

.input-container.col-6 {
  flex: 1;
}

.g-input-label {
  margin-bottom: 8px;
  font-weight: 500;
  color: #495057;
}

.asterisco-obrigatorio {
  color: #e74c3c;
}

.input {
  width: 100%;
}

.p-text-secondary {
  margin-top: 4px;
  font-size: 0.85rem;
  color: #6c757d;
}

.info-percentual {
  margin-top: 12px;
  padding: 12px;
  background: #e3f2fd;
  border-radius: 4px;
  border: 1px solid #90caf9;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1976d2;
}

.info-percentual i {
  font-size: 1.2rem;
}

.info-percentual strong {
  color: #0d47a1;
}

/* Ajustes para componentes PrimeVue */
:deep(.p-inputnumber) {
  width: 100%;
}

:deep(.p-inputtext) {
  width: 100%;
}

:deep(.p-message) {
  width: 100%;
}
</style>
